/**
 * A ~60 line History API router. The app has six routes and no nesting, so a
 * routing dependency would be pure weight.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Tier } from '@geogame/shared';

export type Route =
  | { readonly name: 'home' }
  /**
   * Pick a game, then play it.
   *
   * A ROUTE RATHER THAN A PANEL, because it is a step in starting a match and
   * a step you can go back from. The home screen collapsed to two buttons on
   * the way here, and everything that used to sit beside them -- the modes,
   * the difficulty, the courses, the clock -- lives on this screen, where the
   * person looking at it has already said they want to play.
   */
  | { readonly name: 'play' }
  /**
   * Signing in, as a destination rather than a panel at the bottom of a menu.
   *
   * FOR THE NEW DEVICE, which is the case the panel could not serve. A player
   * who already has an account and opens the game on a new phone lands on
   * `FirstRunScreen` -- one button offering a tutorial they do not need -- and
   * the only other way through was to skip, scroll past a launcher, and read a
   * heading that says "Save my progress" as though it were for somebody with
   * nothing to save.
   */
  | { readonly name: 'signin' }
  | { readonly name: 'queue' }
  | { readonly name: 'match' }
  | { readonly name: 'results' }
  | { readonly name: 'daily' }
  | { readonly name: 'city'; readonly tier: Tier }
  /**
   * Back Roads: a trip through New York in a chosen year. Untimed, offline,
   * and never touches the gateway, so the trip lives in the address and a
   * link to one can be handed to somebody.
   */
  | { readonly name: 'backroads'; readonly tripId: string | null }
  | { readonly name: 'share'; readonly token: string }
  /**
   * Where a sign-in comes back to.
   *
   * BOTH PROVIDERS LAND HERE, and both carry what they carry in the FRAGMENT
   * rather than the query string: a fragment is never sent to a server, never
   * lands in an access log and never rides a Referer header. It is read once
   * and wiped off the address bar.
   */
  | { readonly name: 'auth'; readonly provider: 'magic' | 'google' }
  /**
   * The policy pages. Real routes with stable addresses, because Google
   * will not publish an OAuth app without URLs it can fetch, and because a
   * link that only works from inside the app is not a policy anybody can
   * find.
   */
  /** Coverage, boards and head-to-head. Everything the rollups feed. */
  | { readonly name: 'progress' }
  | { readonly name: 'privacy' }
  | { readonly name: 'terms' }
  | { readonly name: 'not-found'; readonly path: string };

export function parsePath(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { name: 'home' };
  if (path === '/play') return { name: 'play' };
  if (path === '/signin') return { name: 'signin' };
  if (path === '/queue') return { name: 'queue' };
  if (path === '/match') return { name: 'match' };
  if (path === '/results') return { name: 'results' };
  if (path === '/daily') return { name: 'daily' };
  const city = /^\/city\/(explorer|navigator|cartographer|surveyor)$/.exec(path);
  if (city?.[1]) return { name: 'city', tier: city[1] as Tier };
  if (path === '/backroads') return { name: 'backroads', tripId: null };
  const backroads = /^\/backroads\/([a-z0-9-]{1,64})$/.exec(path);
  if (backroads?.[1]) return { name: 'backroads', tripId: backroads[1] };
  // A duel token is `<base64url(payload)>.<base64url(hmac)>` — the dot is part
  // of it, and a signed payload runs to a few hundred characters. A class that
  // omits the dot, or a cap under the real length, sends every share link
  // straight to "Nothing here".
  const share = /^\/d\/([A-Za-z0-9_.-]{8,1024})$/.exec(path);
  if (share?.[1]) return { name: 'share', token: share[1] };
  if (path === '/progress') return { name: 'progress' };
  if (path === '/privacy') return { name: 'privacy' };
  if (path === '/terms') return { name: 'terms' };
  if (path === '/auth/magic') return { name: 'auth', provider: 'magic' };
  if (path === '/auth/google') return { name: 'auth', provider: 'google' };
  return { name: 'not-found', path };
}

export function routeToPath(route: Route): string {
  switch (route.name) {
    case 'home':
      return '/';
    case 'play':
      return '/play';
    case 'signin':
      return '/signin';
    case 'queue':
      return '/queue';
    case 'match':
      return '/match';
    case 'results':
      return '/results';
    case 'daily':
      return '/daily';
    case 'city':
      return `/city/${route.tier}`;
    case 'backroads':
      return route.tripId === null ? '/backroads' : `/backroads/${route.tripId}`;
    case 'share':
      return `/d/${route.token}`;
    case 'progress':
      return '/progress';
    case 'privacy':
      return '/privacy';
    case 'terms':
      return '/terms';
    case 'auth':
      return `/auth/${route.provider}`;
    case 'not-found':
      return route.path;
  }
  /*
   * NO `default`, DELIBERATELY.
   *
   * There was one, returning '/', and it silently swallowed a route somebody
   * had added to `Route` and to `parsePath` but not to here: `navigate` built
   * '/', compared it to the '/' already in the address bar, and returned
   * without doing anything. A button that does nothing at all, and nothing
   * anywhere reports it.
   *
   * Without the default this switch is exhaustive, so the next route that
   * misses a case is a typecheck failure instead of a dead button.
   */
  const unreachable: never = route;
  return unreachable;
}

/** Imperative navigation, usable from the store as well as from components. */
export function navigate(route: Route, opts: { replace?: boolean } = {}): void {
  const path = routeToPath(route);
  if (path === window.location.pathname) return;
  if (opts.replace) window.history.replaceState(null, '', path);
  else window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute(): { route: Route; go: (r: Route) => void } {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((r: Route) => navigate(r), []);
  return { route, go };
}
